/**
 * Application route constants
 */

export const ROUTES = {
  HOME: '/',
  LOGIN: '/login',
  SIGNUP: '/signup',
  SET_PASSWORD: '/set-password',
  FORGOT_PASSWORD: '/forgot-password',
  CHAT: '/chat',
  ORG: '/org',
  MANAGE_STAFF: '/org/staff',
  STAFF_ONBOARDING: '/org/staff-onboarding',
  ACCESS_DENIED: '/access-denied',
  PUBLIC_QUEUE: '/public/queue/:clinicId',
  DOCTOR_PROFILE: '/org/profile',
  PRACTICE_PRESCRIPTION: '/org/practice',
  FEES_BILLING: '/org/fees-billing',
  // Admin-only Settings page — module toggles (Laboratory, Admission/IPD,
  // Procedure autocompletion) moved here out of Fees & Billing. Reachable
  // from the avatar dropdown's "Settings" item (UserProfilePanel).
  SETTINGS: '/org/settings',
  PROCEDURES: '/org/procedures',
  DEPARTMENTS: '/org/departments',
  LAB_CATALOG: '/org/lab/catalog',
  LAB_QUEUE: '/org/lab/queue',
  DOCTOR_RATES: '/org/doctor-rates',
  PROCEDURE_QUEUE: '/org/procedures/queue',
  ACTION_PENDING: '/org/action-pending',
  // Phase 2 — treatment plan authoring
  CREATE_TREATMENT_PLAN: '/org/patients/:patientId/treatment-plans/new',
  // Phase 2.7 — workflow plans (doctor's reusable plan library)
  TREATMENT_PLANS: '/org/treatment-plans',
  // Phase 2.8 — Patient plans (clinic-wide list + per-plan page)
  PATIENT_PLANS: '/org/patient-plans',
  PATIENT_PLAN_DETAIL: '/org/patient-plans/:planId',
  // M1 — ABHA / ABDM health ID
  ABHA: '/org/abha',
  // M3 — ABDM HIU (Health Information User) — external patient history under consent
  HIU_CONSENT_REQUEST: '/org/health-info/request',
  HIU_CONSENTS: '/org/health-info/consents',
  HIU_CONSENT_DETAIL: '/org/health-info/consents/:consentId',
  HIU_CONSENT_RECORDS: '/org/health-info/consents/:consentId/records',
  // M2 — ABDM HIP (Health Information Provider) — publish this clinic's own visit
  // records outward to ABDM. Deliberately distinct from the /org/health-info/{request,
  // consents} paths above (M3) so the two data-flow directions aren't confused at a glance.
  HIP_CARE_CONTEXTS: '/org/health-info/publish',
  // OR-75 — every visit and admission of ONE patient for the last 12 months (same cards as the list).
  HIP_PATIENT_RECORDS: '/org/health-info/publish/patient/:patientId',
  // B2 — IPD config catalogs (admission-gated)
  BED_CATEGORIES: '/org/bed-categories',
  SURGERY_CATALOG: '/org/surgery-catalog',
  OT_ROOMS: '/org/ot-rooms',
  // Day-1 IPD / S1 — Charge Items master (admission-gated, MANAGE_CHARGE_MASTER)
  CHARGE_ITEMS: '/org/charge-items',
  // B3 — ward→room→bed inventory builder + read-only bed-board preview
  BED_SETUP: '/org/bed-setup',
  BED_BOARD: '/org/bed-board',

  // C1 — Admit + assign bed (admission gated)
  ADMISSIONS: '/org/admissions',
  ADMIT_PATIENT: '/org/admissions/new',

  // C2 — Live occupancy board (admission gated, VIEW_OCCUPANCY_BOARD)
  OCCUPANCY_BOARD: '/org/occupancy',

  // S2 — Monthly Collections board (permission-gated ONLY, NOT admission
  // gated — an OPD-only org must reach this route too, §2.4)
  MONTHLY_COLLECTIONS: '/org/collections',

  // BUSY-VOUCHER R3 — "How to import into Busy" guide (opened from the Monthly
  // files section; same permission: DOWNLOAD_BUSY_FILES). Declared as
  // its own deeper path — `/org/collections` stays exact.
  BUSY_IMPORT_GUIDE: '/org/collections/busy-guide',

  // C6b — the IPD tile/page (admission gated, ACCESS_IPD). Diagnostics is
  // its first content, never its identity (D27) — never name this "Diagnostics".
  IPD: '/org/ipd',

  // EPIC PATIENT-CARD / F9 — admin-only patient directory + 360° patient
  // card. `/org/patients/:patientId` is LESS specific than the existing
  // CREATE_TREATMENT_PLAN path above, so it cannot shadow it (react-router
  // ranks matches by specificity, not declaration order — asserted in
  // src/__tests__/routes.patientCard.test.jsx). Any future literal segment
  // under /org/patients must be declared as its own deeper path for the
  // same reason.
  PATIENT_DIRECTORY: '/org/patients',
  PATIENT_CARD: '/org/patients/:patientId',

  // REVIEW-FORM / R1 — in-app "Rate ArogyaKhata". Top-level, NOT under
  // `/org` (D9/build-plan) — the owner's WhatsApp link is
  // dev.arogyakhata.com/review. Still auth-guarded (App.js).
  REVIEW: '/review',
  // OR-138 — About page (data-source attribution). Top-level like REVIEW;
  // every role, from the avatar dropdown's "About" item.
  ABOUT: '/about',

  // ── Legacy aliases (kept for backward compatibility) ──────────
  /** @deprecated Use ROUTES.ORG instead */
  CROW: '/org',
};

// Legacy /crow/* paths that should redirect to /org/*
export const LEGACY_CROW_ROUTES = [
  { from: '/crow', to: ROUTES.ORG },
  { from: '/crow/staff', to: ROUTES.MANAGE_STAFF },
  { from: '/crow/staff-onboarding', to: ROUTES.STAFF_ONBOARDING },
  { from: '/crow/profile', to: ROUTES.DOCTOR_PROFILE },
];

export const AUTH_ROUTES = [ROUTES.HOME, ROUTES.LOGIN, ROUTES.SIGNUP, ROUTES.SET_PASSWORD, ROUTES.FORGOT_PASSWORD];
export const PROTECTED_ROUTES = [ROUTES.CHAT, ROUTES.ORG, ROUTES.STAFF_ONBOARDING];
